import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy as CopyIcon, Edit as EditIcon } from '@mui/icons-material';
import { FeatureList, Feature } from '../types/api';
import { 
  getTotalFeatures, 
  getTotalGroups, 
  createTableHTML, 
  createTableText,
  getPriorityColor,
  getPriorityLabel 
} from '../utils/helpers';
import { featureTableStyles, loadingStyles, emptyStateStyles } from '../styles';
import FeatureRefinementDialog from './FeatureRefinementDialog';

interface FeatureTableProps {
  featureList: FeatureList | null;
  loading?: boolean;
  onCopySuccess?: () => void;
  onFeatureRefined?: (originalDescription: string, newDescription: string, feature: Feature) => void;
  requirements?: string;
}

const FeatureTable: React.FC<FeatureTableProps> = ({ 
  featureList, 
  loading = false, 
  onCopySuccess,
  onFeatureRefined,
  requirements = ''
}) => {
  const [refinementDialog, setRefinementDialog] = useState({
    open: false,
    feature: null as Feature | null,
  });

  const handleCopyToClipboard = async () => {
    if (!featureList) return;

    const htmlContent = createTableHTML(featureList);
    const textContent = createTableText(featureList);

    try {
      // Используем современный Clipboard API с поддержкой HTML
      if (navigator.clipboard && navigator.clipboard.write) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([textContent], { type: 'text/plain' })
        });
        
        await navigator.clipboard.write([clipboardItem]);
        onCopySuccess?.();
      } else {
        // Fallback: копируем только текст
        await navigator.clipboard.writeText(textContent);
        onCopySuccess?.();
      }
    } catch (error) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = textContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      onCopySuccess?.();
      }
    };

  const handleFeatureClick = (feature: Feature) => {
    setRefinementDialog({
      open: true,
      feature,
    });
  };

  const handleRefinementClose = () => {
    setRefinementDialog({
      open: false,
      feature: null,
    });
  };

  const handleFeatureRefined = (originalDescription: string, newDescription: string) => {
    if (refinementDialog.feature && onFeatureRefined) {
      onFeatureRefined(originalDescription, newDescription, refinementDialog.feature);
    }
  };

  if (loading) {
    return (
      <Box sx={loadingStyles.container}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  if (!featureList || !featureList.groups || featureList.groups.length === 0) {
    return (
      <Box sx={emptyStateStyles.container}>
        <Typography variant="h6" sx={emptyStateStyles.text}>
          Нет данных для отображения
        </Typography>
      </Box>
    );
  }

  const totalFeatures = getTotalFeatures(featureList);
  const totalGroups = getTotalGroups(featureList);

  return (
    <Box>
      <Box sx={featureTableStyles.headerContainer}>
        <Typography variant="h5" sx={featureTableStyles.title}>
          {featureList.title}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={handleCopyToClipboard}
          sx={featureTableStyles.copyButton}
        >
          Копировать таблицу
        </Button>
      </Box>
      
      <Paper elevation={2} sx={featureTableStyles.tablePaper}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Группа</strong></TableCell>
                <TableCell><strong>№</strong></TableCell>
                <TableCell><strong>Название фичи</strong></TableCell>
                <TableCell><strong>Описание</strong></TableCell>
                <TableCell><strong>Приоритет</strong></TableCell>
                <TableCell><strong>Ограничения</strong></TableCell>
                <TableCell><strong>Действия</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {featureList.groups.map((group) => 
                group.features.map((feature, index) => (
                  <TableRow 
                    key={`${group.groupNumber}-${feature.featureNumber}`} 
                    hover
                    sx={featureTableStyles.tableRowCombined(index)}
                    onClick={() => handleFeatureClick(feature)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={featureTableStyles.groupCell}>
                        {group.groupName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={featureTableStyles.featureNumberCell}>
                        {feature.featureNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={featureTableStyles.featureNameCell}>
                        {feature.featureName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={featureTableStyles.descriptionCell}>
                        {feature.featureDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityLabel(feature.priority)}
                        color={getPriorityColor(feature.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={featureTableStyles.restrictionsCell}>
                        {feature.restrictions}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Доуточнить фичу">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFeatureClick(feature);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <Box sx={featureTableStyles.footerContainer}>
        <Typography variant="body2" sx={featureTableStyles.footerText}>
          Всего групп: {totalGroups} | Всего фич: {totalFeatures}
        </Typography>
        <Typography variant="body2" sx={featureTableStyles.footerText}>
          Нажмите на фичу или кнопку редактирования для доуточнения
        </Typography>
      </Box>

      <FeatureRefinementDialog
        open={refinementDialog.open}
        onClose={handleRefinementClose}
        feature={refinementDialog.feature}
        requirements={requirements}
        onRefine={handleFeatureRefined}
        loading={loading}
      />
    </Box>
  );
};

export default FeatureTable; 